import React, { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { io, Socket } from 'socket.io-client'
import { useSelector, useDispatch } from 'react-redux'
import { AppDispatch, RootState } from '../store'
import { dispatchSystemNotification } from '../utils/issueNotificationDispatch'
import { safeFormatDate } from '../utils/dateFormat'

// Same 'MMM dd, yyyy' formatting used across the Leaves feature (LeavesPage,
// OnLeaveToday, ReportsPage) - these toast payloads carry raw ISO datetimes.
const formatLeaveDate = (value: string) => safeFormatDate(value, 'MMM dd, yyyy', value)

interface SocketContextType {
  socket: Socket | null
  isConnected: boolean
  joinProject: (projectId: string) => void
  leaveProject: (projectId: string) => void
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
  joinProject: () => {},
  leaveProject: () => {},
})

export const useSocket = () => useContext(SocketContext)

interface SocketProviderProps {
  children: React.ReactNode
}

// Get backend URL once (memoized outside component to avoid recalculation)
const getBackendUrl = (): string => {
  const metaEnv = import.meta as any
  const envUrl = metaEnv.env?.VITE_API_URL
  
  // If environment variable is set, use it (highest priority)
  if (envUrl) {
    return envUrl
  }
  
  // In production, derive from current window location
  // This ensures WebSocket connections work on the same domain
  if (typeof window !== 'undefined') {
    // Use same origin for production (backend should be on same domain or proxied)
    // For development, fallback to localhost:5000
    const isDevelopment = window.location.hostname === 'localhost' || 
                          window.location.hostname === '127.0.0.1' ||
                          window.location.hostname === ''
    
    if (isDevelopment) {
      return 'http://localhost:5000'
    }
    
    // In production, use the same origin (backend should be proxied or on same domain)
    // The API uses relative URLs (/api), so socket should use same origin
    // If backend is on a different subdomain/port, you MUST set VITE_API_URL environment variable
    const backendUrl = window.location.origin
    console.log('Socket connecting to:', backendUrl)
    return backendUrl
  }
  
  // Fallback for SSR or other cases
  return 'http://localhost:5000'
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [currentProject, setCurrentProject] = useState<string | null>(null)
  const joinTimeoutRef = useRef<number | null>(null)
  const socketRef = useRef<Socket | null>(null)
  const { isAuthenticated, token } = useSelector((state: RootState) => state.auth)
  const dispatch = useDispatch<AppDispatch>()
  
  // Memoize backend URL to avoid recalculation
  const backendUrl = useMemo(() => getBackendUrl(), [])

  useEffect(() => {
    // Only create socket if user is authenticated
    if (!isAuthenticated || !token) {
      // Clean up existing socket if user logs out
      if (socketRef.current) {
        socketRef.current.removeAllListeners()
        socketRef.current.close()
        socketRef.current = null
        setSocket(null)
        setIsConnected(false)
      }
      return
    }

    // Prevent creating multiple socket instances
    if (socketRef.current) {
      // If socket exists and is still active, don't create a new one
      if (socketRef.current.connected || socketRef.current.active) {
        return
      }
      // If socket exists but is disconnected, clean it up first
      socketRef.current.removeAllListeners()
      socketRef.current.close()
      socketRef.current = null
    }

    const newSocket = io(backendUrl, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 3, // Reduced from 5 to prevent too many attempts
      reconnectionDelay: 2000, // Increased delay between attempts
      reconnectionDelayMax: 10000,
      timeout: 20000,
      forceNew: false, // Reuse existing connection if available
      auth: {
        token: token, // Send JWT token for authentication
      },
    })

    // Store socket reference
    socketRef.current = newSocket
    setSocket(newSocket)

    // Event handlers (defined as named functions for proper cleanup)
    const handleConnect = () => {
      console.log('Connected to Socket.io server at:', backendUrl)
      setIsConnected(true)
    }

    const handleDisconnect = () => {
      console.log('Disconnected from Socket.io server')
      setIsConnected(false)
    }

    const handleConnectError = (error: Error) => {
      // Log connection errors with more detail
      console.error('Socket connection error:', {
        message: error.message,
        backendUrl: backendUrl,
        error: error
      })
      
      // Log authentication errors specifically
      if (error.message?.includes('Authentication error')) {
        console.warn('Socket authentication failed:', error.message)
        // Don't attempt to reconnect if authentication fails
        newSocket.disconnect()
      }
      setIsConnected(false)
      // Socket.io will handle reconnection automatically for other errors
    }

    // System-wide broadcasts (e.g. the daily time-log reminder) - app-wide,
    // not tied to any specific page/issue, unlike the issue-scoped listeners
    // in useIssueNotifications.ts.
    const handleSystemNotification = (data: { message: string }) => {
      dispatchSystemNotification(dispatch, data.message)
    }

    // Leave approve/reject - requester-facing, app-wide like system-notification
    // above, since the requester may be anywhere in the app when it's actioned.
    const handleLeaveApproved = (data: { fromDate: string; toDate: string }) => {
      dispatchSystemNotification(dispatch, `Your leave request (${formatLeaveDate(data.fromDate)} – ${formatLeaveDate(data.toDate)}) was approved.`)
    }
    const handleLeaveRejected = (data: { fromDate: string; toDate: string; rejectionReason: string }) => {
      dispatchSystemNotification(dispatch, `Your leave request (${formatLeaveDate(data.fromDate)} – ${formatLeaveDate(data.toDate)}) was rejected: ${data.rejectionReason}`)
    }

    // Attach event listeners
    newSocket.on('connect', handleConnect)
    newSocket.on('disconnect', handleDisconnect)
    newSocket.on('connect_error', handleConnectError)
    newSocket.on('system-notification', handleSystemNotification)
    newSocket.on('leave-approved', handleLeaveApproved)
    newSocket.on('leave-rejected', handleLeaveRejected)

    // Cleanup function
    return () => {
      // Clear any pending join timeout
      if (joinTimeoutRef.current) {
        clearTimeout(joinTimeoutRef.current)
        joinTimeoutRef.current = null
      }
      
      // Remove all event listeners
      newSocket.off('connect', handleConnect)
      newSocket.off('disconnect', handleDisconnect)
      newSocket.off('connect_error', handleConnectError)
      newSocket.off('system-notification', handleSystemNotification)
      newSocket.off('leave-approved', handleLeaveApproved)
      newSocket.off('leave-rejected', handleLeaveRejected)
      
      // Close socket connection
      if (newSocket.connected || newSocket.active) {
        newSocket.close()
      }
      
      socketRef.current = null
      setSocket(null)
    }
  }, [isAuthenticated, token, backendUrl, dispatch])

  // Memoized joinProject callback to prevent unnecessary re-renders
  const joinProject = useCallback((projectId: string) => {
    const currentSocket = socketRef.current
    if (currentSocket && isConnected && currentProject !== projectId) {
      // Clear any existing timeout
      if (joinTimeoutRef.current) {
        clearTimeout(joinTimeoutRef.current)
        joinTimeoutRef.current = null
      }
      
      // Debounce join operations
      const timeout = window.setTimeout(() => {
        // Leave current project if different
        if (currentProject) {
          currentSocket.emit('leave-project', currentProject)
          console.log(`Left project room: ${currentProject}`)
        }
        
        // Join new project
        currentSocket.emit('join-project', projectId)
        console.log(`Joined project room: ${projectId}`)
        setCurrentProject(projectId)
        joinTimeoutRef.current = null
      }, 200)
      
      joinTimeoutRef.current = timeout
    }
  }, [isConnected, currentProject])

  // Memoized leaveProject callback to prevent unnecessary re-renders
  const leaveProject = useCallback((projectId: string) => {
    const currentSocket = socketRef.current
    if (currentSocket && isConnected && currentProject === projectId) {
      currentSocket.emit('leave-project', projectId)
      console.log(`Left project room: ${projectId}`)
      setCurrentProject(null)
    }
  }, [isConnected, currentProject])

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({ socket, isConnected, joinProject, leaveProject }),
    [socket, isConnected, joinProject, leaveProject]
  )

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  )
}
